// Spinning Wheel Challenges - 6 topics, 5 MCQs each
// Answer format: correctIndex (0-based)

export const CHALLENGE_TOPICS = [
  {
    id: 'arrays',
    label: 'Arrays',
    color: '#FFB800',
    emoji: '📊',
    questions: [
      {
        q: 'What is the time complexity to access an element in an array by index?',
        options: ['O(1)', 'O(log n)', 'O(n)', 'O(n²)'],
        correctIndex: 0,
        explanation: 'Array access by index is constant time because elements are stored contiguously in memory.'
      },
      {
        q: 'Which is TRUE about Java arrays?',
        options: [
          'Arrays can grow dynamically',
          'Arrays have a fixed size once created',
          'Arrays store elements of different types',
          'Arrays use pointers like C'
        ],
        correctIndex: 1,
        explanation: 'Java arrays have fixed length once created. Use ArrayList for dynamic sizing.'
      },
      {
        q: 'What is the output of nums[nums.length - 1] for nums = {10, 20, 30}?',
        options: ['10', '20', '30', 'IndexOutOfBoundsException'],
        correctIndex: 2,
        explanation: 'nums.length - 1 = 2 → nums[2] = 30 (the last element).'
      },
      {
        q: 'How many comparisons does linear search need in the WORST case for n elements?',
        options: ['1', 'log n', 'n', 'n²'],
        correctIndex: 2,
        explanation: 'Worst case is when target is last or not present — requires n comparisons.'
      },
      {
        q: 'To copy an int array, which is correct?',
        options: [
          'int[] copy = original;',
          'int[] copy = original.clone();',
          'int[] copy = new int(original);',
          'int[] copy = original.copyOf();'
        ],
        correctIndex: 1,
        explanation: 'original.clone() creates a shallow copy. Assignment (=) copies only the reference.'
      }
    ]
  },
  {
    id: 'two-pointer',
    label: 'Two Pointer',
    color: '#007AFF',
    emoji: '👉',
    questions: [
      {
        q: 'What is the space complexity of two-pointer technique?',
        options: ['O(1)', 'O(log n)', 'O(n)', 'O(n²)'],
        correctIndex: 0,
        explanation: 'Two-pointer uses only 2 index variables — constant extra space.'
      },
      {
        q: 'In "Container With Most Water", when do we move the LEFT pointer?',
        options: [
          'Always',
          'When height[left] > height[right]',
          'When height[left] < height[right]',
          'Randomly'
        ],
        correctIndex: 2,
        explanation: 'We move the pointer with smaller height inward — moving the taller one cannot increase area.'
      },
      {
        q: 'For "Remove Duplicates from Sorted Array", the SLOW pointer tracks:',
        options: [
          'The position of the current unique element',
          'The end of the array',
          'The middle element',
          'The index of duplicates'
        ],
        correctIndex: 0,
        explanation: 'Slow tracks where the next unique element should be placed.'
      },
      {
        q: 'Two-pointer works BEST on which type of arrays?',
        options: [
          'Randomly ordered arrays',
          'Sorted arrays or arrays where symmetry can be exploited',
          'Arrays with all zeros',
          'Only 2D arrays'
        ],
        correctIndex: 1,
        explanation: 'Two-pointer thrives when array structure (sorted, palindromic, symmetric) gives us a directional decision.'
      },
      {
        q: 'Trapping Rain Water — water at index i is bounded by:',
        options: [
          'The tallest bar in the array',
          'The shortest bar in the array',
          'min(leftMax, rightMax) - height[i]',
          'max(leftMax, rightMax) - height[i]'
        ],
        correctIndex: 2,
        explanation: 'Water level equals the shorter of the two tallest walls minus the current height.'
      }
    ]
  },
  {
    id: 'sorting',
    label: 'Sorting',
    color: '#22C55E',
    emoji: '📈',
    questions: [
      {
        q: 'What is the average time complexity of Quick Sort?',
        options: ['O(n)', 'O(n log n)', 'O(n²)', 'O(log n)'],
        correctIndex: 1,
        explanation: 'Quick Sort averages O(n log n). Worst case is O(n²) on already-sorted or bad pivots.'
      },
      {
        q: 'Which sort is GUARANTEED O(n log n) worst case?',
        options: ['Bubble Sort', 'Quick Sort', 'Merge Sort', 'Insertion Sort'],
        correctIndex: 2,
        explanation: 'Merge Sort always splits in half and merges — guaranteed O(n log n).'
      },
      {
        q: 'Bubble Sort BEST case time complexity (with early-exit optimization)?',
        options: ['O(1)', 'O(n)', 'O(n log n)', 'O(n²)'],
        correctIndex: 1,
        explanation: 'If already sorted, one pass with no swaps triggers early exit → O(n).'
      },
      {
        q: 'Which sorting algorithm is STABLE by default?',
        options: ['Quick Sort', 'Heap Sort', 'Merge Sort', 'Selection Sort'],
        correctIndex: 2,
        explanation: 'Merge Sort preserves the relative order of equal elements.'
      },
      {
        q: 'In Quick Sort, the partition step returns:',
        options: [
          'The smallest element',
          'The final index of the pivot after partitioning',
          'The middle index',
          'The last element'
        ],
        correctIndex: 1,
        explanation: 'Partition places pivot in its final sorted position and returns that index for recursive calls.'
      }
    ]
  },
  {
    id: 'searching',
    label: 'Searching',
    color: '#06B6D4',
    emoji: '🔍',
    questions: [
      {
        q: 'Binary Search REQUIRES the array to be:',
        options: ['Reversed', 'Sorted', 'Unique', 'Positive numbers only'],
        correctIndex: 1,
        explanation: 'Binary Search needs sorted order to decide left or right half.'
      },
      {
        q: 'What is the time complexity of Binary Search?',
        options: ['O(1)', 'O(log n)', 'O(n)', 'O(n log n)'],
        correctIndex: 1,
        explanation: 'Binary Search halves the search space each step → log₂(n) iterations.'
      },
      {
        q: 'Why do we compute mid = left + (right - left) / 2 instead of (left + right) / 2?',
        options: [
          'To make it faster',
          'To avoid integer overflow',
          'To handle floats',
          'It gives a different result'
        ],
        correctIndex: 1,
        explanation: '(left + right) can overflow int max. left + (right - left)/2 is safer for very large arrays.'
      },
      {
        q: 'In "Search in Rotated Sorted Array", how do we decide which half to search?',
        options: [
          'Random guess',
          'By checking which half is sorted, then whether target lies in it',
          'Always go left',
          'Always go right'
        ],
        correctIndex: 1,
        explanation: 'At each step, one half is always sorted. Check if target lies in it, else search the other half.'
      },
      {
        q: 'Find Peak Element uses which principle?',
        options: [
          'Always go left',
          'Always move toward the higher neighbor (uphill)',
          'Compare with target',
          'Binary sum'
        ],
        correctIndex: 1,
        explanation: 'If nums[mid] < nums[mid+1], peak lies right (upward slope). This guarantees convergence.'
      }
    ]
  },
  {
    id: 'recursion',
    label: 'Recursion',
    color: '#A855F7',
    emoji: '🧙',
    questions: [
      {
        q: 'Every recursive function MUST have:',
        options: ['A loop', 'A base case', 'A global variable', 'An array parameter'],
        correctIndex: 1,
        explanation: 'Without a base case, recursion never terminates → StackOverflowError.'
      },
      {
        q: 'For Tower of Hanoi with n disks, minimum number of moves is:',
        options: ['n', 'n²', '2^n - 1', '2n'],
        correctIndex: 2,
        explanation: 'Provably optimal: 2^n - 1 moves. For 3 disks = 7, 4 disks = 15, etc.'
      },
      {
        q: 'Naive recursive Fibonacci fib(n) has time complexity:',
        options: ['O(n)', 'O(n log n)', 'O(2^n)', 'O(n²)'],
        correctIndex: 2,
        explanation: 'Each call branches into 2 more. Tree has ~2^n nodes without memoization.'
      },
      {
        q: 'Memoization converts naive recursion into:',
        options: ['Iteration', 'A hash table', 'Dynamic Programming (top-down)', 'A tree'],
        correctIndex: 2,
        explanation: 'Memoization is the top-down DP technique — cache results to avoid recomputation.'
      },
      {
        q: 'What happens when a recursive call is made?',
        options: [
          'The current function pauses and a new stack frame is pushed',
          'The current function ends',
          'A new thread is created',
          'The array is copied'
        ],
        correctIndex: 0,
        explanation: 'Each recursive call adds a new activation record to the call stack.'
      }
    ]
  },
  {
    id: 'graphs',
    label: 'Graphs & Trees',
    color: '#EC4899',
    emoji: '🌐',
    questions: [
      {
        q: 'BFS uses which data structure?',
        options: ['Stack', 'Queue', 'Priority Queue', 'HashMap'],
        correctIndex: 1,
        explanation: 'BFS uses a FIFO queue to visit nodes level-by-level.'
      },
      {
        q: 'DFS uses which data structure?',
        options: ['Queue', 'Priority Queue', 'Stack (or recursion)', 'HashSet'],
        correctIndex: 2,
        explanation: 'DFS uses a LIFO stack (explicit or via recursion call stack).'
      },
      {
        q: 'Dijkstra\'s algorithm does NOT work with:',
        options: [
          'Positive weights',
          'Zero weights',
          'Negative weights',
          'Directed graphs'
        ],
        correctIndex: 2,
        explanation: 'Negative weights break Dijkstra\'s greedy invariant. Use Bellman-Ford for negative edges.'
      },
      {
        q: 'Inorder traversal of a BST produces values in what order?',
        options: [
          'Random order',
          'Descending order',
          'Ascending (sorted) order',
          'Insertion order'
        ],
        correctIndex: 2,
        explanation: 'Left → Root → Right on a BST yields sorted ascending order.'
      },
      {
        q: 'LCA (Lowest Common Ancestor) recursion returns a node when:',
        options: [
          'Node is null',
          'Node equals p or q, or both subtrees returned non-null',
          'Node has no children',
          'Node has 2 children'
        ],
        correctIndex: 1,
        explanation: 'Base: null or match. Recursive: if both sides find something, current node is LCA.'
      }
    ]
  }
];

export const getRandomTopicIndex = () => Math.floor(Math.random() * CHALLENGE_TOPICS.length);
